import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, Calendar, User } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import Footer from "@/components/Footer";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await api.get(`/blog/${slug}`);
        if (!cancelled) {
          setPost(data);
          document.title = `${data.title} · Careable Blog`;
        }
      } catch (err) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <div
      data-testid="blog-post-page"
      className="min-h-screen bg-[#F4FAF8] text-[#0E2A36] flex flex-col"
    >
      <header className="bg-[#1F4E5F] text-[#F4FAF8]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <Link
            data-testid="blog-post-back"
            to="/blog"
            className="inline-flex items-center gap-1.5 text-sm text-[#F4FAF8]/75 hover:text-[#F4FAF8] mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> All posts
          </Link>
          {post && (
            <>
              <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
                {post.title}
              </h1>
              <div className="mt-4 flex items-center gap-4 flex-wrap text-[13px] text-[#F4FAF8]/75">
                {post.author && (
                  <span className="inline-flex items-center gap-1.5">
                    <User className="w-4 h-4" /> {post.author}
                  </span>
                )}
                {post.published_at && (
                  <span className="inline-flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" /> {fmtDate(post.published_at)}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {loading ? (
          <div className="py-20 flex items-center justify-center text-[#6A8290] text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : notFound ? (
          <div data-testid="blog-post-notfound" className="py-20 text-center">
            <p className="font-display text-xl font-bold">Post not found</p>
            <p className="text-sm text-[#6A8290] mt-2">
              This story may have been moved or unpublished.
            </p>
            <Link
              to="/blog"
              className="mt-6 inline-flex items-center gap-2 bg-[#1F4E5F] text-[#F4FAF8] px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#173E50]"
            >
              Back to blog
            </Link>
          </div>
        ) : post ? (
          <article
            data-testid="blog-post-content"
            className="bg-white border border-[#C9DDD6] rounded-3xl p-6 sm:p-8 lg:p-10 shadow-[0_2px_18px_rgba(15,56,32,0.04)]"
          >
            {post.cover_image_url && (
              <div className="rounded-2xl overflow-hidden mb-7">
                <img
                  src={post.cover_image_url}
                  alt={post.title}
                  className="w-full object-cover"
                />
              </div>
            )}
            <div className="prose prose-careable max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
            </div>
          </article>
        ) : null}
      </main>
      <Footer />
    </div>
  );
}
